import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList, TemplateRef,
  ViewChildren
} from '@angular/core';
import {
  ClassificatorsQuery,
  ClassificatorValuesQuery, ConfirmationDialogService, CONSTANTS,
  FilterModel, GLOBAL,
  LanguagesQuery, LeModalService,
  PropertiesQuery,
  UnitsQuery,
  UsersQuery
} from "common-shared";
import {LazyLoadEvent, TableState} from "primeng/api";
import {BehaviorSubject, debounceTime, distinctUntilChanged, Observable, skip, Subject} from "rxjs";
import {map, takeUntil} from "rxjs/operators";
import {TableComponent} from "common-shared";
import {ItemGroupQuery, ItemListFilter, ItemQuery, ItemTableSelectableColumns} from "web-shared";
import {ItemListQuery} from "web-shared";
import {ItemGroupsQuery} from "web-shared";
import {
  ManufacturingGraphModalComponent
} from "../../graph/manufacturing-graph-modal/manufacturing-graph-modal.component";
import {
  ManufacturingItemGroupEditModalComponent
} from "../../manufactured-item-groups/manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {
  ManufacturingItemEditModalComponent
} from "../manufacturing-item-edit-modal/manufacturing-item-edit-modal.component";
import {SpecLineModel} from "web-shared";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {ItemService} from "../../../common/item/item.service";
import {NgSelectComponent} from '@ng-select/ng-select';
import {ColumnFilter} from "primeng/table";

@Component({
  selector: 'app-item-table',
  templateUrl: './item-table.component.html',
  styles: []
})
export class ItemTableComponent extends TableComponent implements OnInit, AfterViewInit {
  @Input() filter: FilterModel<ItemListFilter>;
  @Input() stateKey: string = 'state-local';
  @Input() hasBulkEdit = false;
  @Input() selectionMode: 'single' | 'multiple' = 'single';
  @Input() selectedId: string | string[];
  @Input() filter$ = new BehaviorSubject<boolean>(false);
  @Input() statusCl: string = CONSTANTS.ItemStatus.ClassificatorId;
  @Input() showOnlyRelevantSpecsChange: boolean;
  @Input() includeInheritedValuesChange: boolean;
  @Input() includeSpecConstraintsValuesChange: boolean;
  @Input() urlForEdit: string;
  @Input() rowSelectEnabled: boolean = false;
  filteredData$ = new BehaviorSubject<ItemListQuery[]>([]);
  selectedItem: ItemListQuery | ItemListQuery[];
  @Output() onSelect = new EventEmitter<string | string[]>();
  @Input() onShowGraph: BehaviorSubject<[ItemListQuery, FilterModel<ItemListFilter>]>;
  itemListPropertyId = GLOBAL.Properties.ItemListFilterSettings;
  showBulkEdit = false;
  isClearingState = false;
  bulkEditSpecs: SpecLineModel[] = [];
  updateMarker$ = new Subject();
  statusCls$ = new Observable<string[]>();
  selectableColumns = new ItemTableSelectableColumns();
  specSelectionToggles = {
    showOnlyRelevantSpecsChange: false,
    includeInheritedValuesChange: false,
    IncludeSpecConstraintsValuesChange: false,
  }

  @ViewChildren(ColumnFilter) private columnFilterComponentQuery: QueryList<ColumnFilter>;
  columnFilterComponents: ColumnFilter[];
  @ContentChild('buttonsTemplate') buttonsTemplate: TemplateRef<any>;

  constructor(public itemService: ItemService, public itemGroupsQuery: ItemGroupsQuery, public propertiesQuery: PropertiesQuery, public classificatorValuesQuery: ClassificatorValuesQuery, public usersQuery: UsersQuery, public unitsQuery: UnitsQuery, private modalService: LeModalService, public languagesQuery: LanguagesQuery, private confirmationDialogService: ConfirmationDialogService, private classificatorsQuery: ClassificatorsQuery, private changeDetector: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.loadDebounceTime = 300;
    this.search$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      if (this.table) {
        this.table.filterGlobal(value, 'contains');
      }
    });

    this.statusCls$ = this.classificatorsQuery.getWithParentIds(this.statusCl);

    this.lazyLoadStream.pipe(takeUntil(this.componentDestroyed), debounceTime(this.loadDebounceTime), distinctUntilChanged(), map((value) => {
      this.loadDebounceTime = 0;
      return value;
    })).subscribe(event => this.load(event));
    this.applyDefaultFilters();

    this.itemService.getAll$.pipe(skip(1), map(() => this.updateMarker$.next(true)), takeUntil(this.componentDestroyed)).subscribe();

    this.filter$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      if (value) {
        this.tryLazyLoad();
      }
    });

    this.setInitialColumnVisibility();

    this.table.onStateSave.pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      if (!this.isClearingState) {
        this.setInitialColumnVisibility();
      } else {
        this.selectableColumns = new ItemTableSelectableColumns();
      }
      this.isClearingState = false
    });
  }

  ngAfterViewInit() {
    this.columnFilterComponentQuery.changes.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.columnFilterComponents = [...value._results];
      this.changeDetector.detectChanges();
    });
  }

  load(event: LazyLoadEvent = null) {
    this.prepareLoad(this.filter, event);
    this.filter.VisibleColumns = Object.keys(this.selectableColumns).map(value => this.selectableColumns[value] == true ? value : null).filter(value => value != null);
    this.itemService.getByFilter(this.filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
      this.filteredData$.next(pagedData.Data);
      this.updateAfterLoad(this.filter, pagedData);
      this.updateMarker$.next(false);
      this.ensureSelection(pagedData.Data);
    });
  }

  onStateRestore(state: TableState) {
    state.selection = null;

    setTimeout(() => {
      this.tableSortField = state.sortField ?? this.tableSortField ?? this.sortField;
      this.tableSortOrder = state.sortOrder ?? this.tableSortOrder ?? this.sortOrder;
      this.hasFilters = FilterModel.hasAny(state.filters);
      if (this.hasFilters) {
        this.filter.AdvancedFilter = true;
      }
    });
  }

  clearState() {
    this.table.clearState();
    this.filter.Data = new ItemListFilter({ForSelect: this.filter.Data.ForSelect, GroupByDataset: this.filter.Data.GroupByDataset});
    this.filter.Global = null;
    this.applyDefaultFilters();
    this.table.filters["ShowOnlyRelevantSpecs"] = {value: false};
    this.table.filters["IncludeInheritedSpecs"] = {value: false};
    this.table.filters["IncludeSpecConstraints"] = {value: false};
    setTimeout(() => {
      this.tryLazyLoad();
    });
    this.isClearingState = true
  }

  loadSavedState(state: LazyLoadEvent) {
    this.filter.Data = new ItemListFilter({ForSelect: this.filter.Data.ForSelect, GroupByDataset: this.filter.Data.GroupByDataset});
    this.table.clearState();
    this.table.filters = state.filters ?? {};
    this.onStateRestore(state);
    const doesNotTriggerLazyLoad = this.tableSortOrder === state.sortOrder && this.tableSortField === state.sortField;
    if (doesNotTriggerLazyLoad) {
      this.load(state);
    }
    this.table.saveState();
  }

  showGraph(row: ItemListQuery) {
    if (this.onShowGraph) {
      this.onShowGraph.next([row, this.filter]);
    } else {
      this.modalService.show(ManufacturingGraphModalComponent, {initialState: {id: row.Id, graphSource: 'Item', title: row.DisplayName} as ManufacturingGraphModalComponent, class: 'modal-full'});
    }
  }

  open(row: ItemQuery) {
    let masterItemId: string = null;
    if (row.Code === this.itemGroupsQuery.getEntity(row.ItemGroupId).Code) {
      masterItemId = row.ItemGroupId;
    }
    let modalRef = null;
    if (masterItemId) {
      modalRef = this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: masterItemId} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl'});
    } else {
      modalRef = this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: row.Id} as ManufacturingItemEditModalComponent, class: 'modal-xl'});
    }
    modalRef.onHide$.subscribe(value => {
      if (value.result) {
        this.tryLazyLoad();
      }
    })
  }

  onTableDropdownOpen(isOpenEvent: boolean) {
    if (isOpenEvent) {
      this.reloadOnClose = false;
    } else {
      this.tableDropdownClosed.emit();
      if (this.reloadOnClose) {
        this.tryLazyLoad();
      }
    }
  }

  onRowSelect() {
    if (!this.rowSelectEnabled) {
      if (this.selectionMode === 'single') {
        this.onSelect.emit((this.selectedItem as ItemListQuery).Id);
      } else {
        this.onSelect.emit([...(this.selectedItem as ItemListQuery[]).map(value => value.Id)]);
      }
    }
  }

  tryLazyLoad() {
    if (typeof this.table.filter === 'function') {
      this.table._filter();
    } else {
      this.load();
    }
  }

  addBulkEditSpec(propertyId: string) {
    let spec = new SpecLineModel({PropertyId: propertyId});
    this.bulkEditSpecs.push(spec);
    this.bulkEditSpecs = [...this.bulkEditSpecs];
  }

  removeBulkEditSpec(bulkEditSpec: SpecLineModel) {
    this.bulkEditSpecs = this.bulkEditSpecs.filter(spec => spec.Id !== bulkEditSpec.Id);
    this.bulkEditSpecs = [...this.bulkEditSpecs];
  }

  applyBulkEdit() {
    this.bulkEditSpecs = this.bulkEditSpecs.filter(spec => this.filter.Data.SelectedSpecs.includes(spec.PropertyId));
    this.confirmationDialogService.confirmTranslated(
      marker('ime.manufacturing.manufacturing-item.Apply changes?'),
      marker('ime.manufacturing.manufacturing-item.This will edit all items in the list'), {yesLabel: 'OK', noLabel: 'Cancel'}).then(result => {
      if (result) {
        this.itemService.applyBulkEdit(this.filter, this.bulkEditSpecs).pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
          console.log(value);
        });
      }
    });
  }

  private ensureSelection(items: ItemListQuery[]) {
    if (this.selectionMode == 'single') {
      if (this.selectedId) {
        this.selectedItem = items.find(value => value.Id == this.selectedId);
      }
    } else {
      let selectedIds = this.selectedId as string[];
      this.selectedItem = [...items.filter(value => selectedIds.includes(value.Id))];
    }
  }

  setInitialColumnVisibility() {
    const storedTableState = this.getTableState(this.stateKey);
    if (storedTableState) {
      if (storedTableState.visibleFields) {
        this.selectableColumns = storedTableState.visibleFields;
      } else {
        storedTableState.visibleFields = this.selectableColumns;
        this.setTableState(this.stateKey, storedTableState);
      }
    }
  }

  updateTableColumnState() {
    const tableState = this.getTableState(this.stateKey) ?? {};
    tableState.visibleFields = this.selectableColumns;
    this.setTableState(this.stateKey, tableState);
  }

  groupByFn(item) {
    return item.From[0] ?? undefined;
  }

  groupValueFn = (_: string, children: any[]) => (this.itemGroupsQuery.getEntity(_));

  selectFilteredChildren(toItemGroupSelect: NgSelectComponent, item: ItemGroupQuery) {
    let ngOptions = toItemGroupSelect.itemsList.filteredItems.filter(value => value.parent?.value == item);
    for (let ngOption of ngOptions) {
      toItemGroupSelect.select(ngOption);
    }
  }
}
